/**
 * CCO › Gestor: o que a ronda de 15 minutos encontrou e o diagnóstico do gestor (US-3401 a US-3403) — Nexora Grid.
 *
 * Criado em: 26/10/2026 · Última modificação: 26/10/2026 · Versão: 1.2.0
 *
 * Um cartão por gatilho aberto (desvio do normal do dia da semana, alarme crítico/alto, achado do gêmeo, sem
 * comunicação), por prioridade, com desde quando, quantas rondas viram e a vez do gestor (investigando, diagnóstico,
 * sem orçamento). "Ver diagnóstico" abre o que o gestor escreveu: hipóteses, confiança, limitações e fontes. O
 * diagnóstico é só sugestão: decisão e manobra continuam no CCO Clássico. A engenharia e a supervisão (`editar_regras`)
 * ajustam o limiar da ronda por medida.
 * v1.1 (US-3404): "Ajudou?" (útil / não útil) no diagnóstico, quanto tempo levou para normalizar e os indicadores do
 * gestor (utilidade, falsos alertas, tempo até normalizar, antecedência e custo) nos últimos 30 dias.
 */
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { api, ErroApi } from "../api";
import { useCco } from "./contexto";
import { FORMA, NOME_PRIORIDADE, type Prioridade } from "./medidas";

type Gatilho = { id: string; tipo: "desvio" | "alarme" | "gemeo" | "sem_comunicacao" | "outorga"; prioridade: Prioridade; setor: string | null;
  device_id: string | null; medida: string | null; resumo: string; aberto_em: string; ultima_deteccao: string; deteccoes: number;
  encerrado_em: string | null; investigacao: "pendente" | "em_andamento" | "concluida" | "sem_orcamento" | "dispensada" };
type Investigacao = { criado_em: string; situacao: "concluida" | "sem_orcamento" | "falhou"; texto: string; confianca: string | null;
  hipoteses: string[]; limitacoes: string[]; fontes: string[]; custo_usd: number; modelo: string; erro: string | null };
type Detalhe = Gatilho & { investigacoes: Investigacao[]; duracao_min: number | null; meu_feedback: boolean | null; pode_opinar: boolean };
type Indicadores = { dias: number; gatilhos: number; encerrados: number; mediana_min_ate_normalizar: number | null; curtos: number;
  feedback: { util: number; nao_util: number; taxa_utilidade_pct: number | null }; falsos_alertas: number;
  antecedencia: { casos: number; mediana_min: number | null }; investigados: number; custo_investigacoes_usd: number };
type Limiar = { medida: string; desvio_min: number; janelas_seguidas: number; ativo: boolean; motivo: string; atualizado_por: string };
type Limiares = { padrao: { desvio_min: number; janelas_seguidas: number }; limiares: Limiar[]; pode_editar: boolean };
type Execucao = { janela: string; concluida_em: string | null; abertos: number; erro: string | null };

const TIPO: Record<Gatilho["tipo"], string> = { desvio: "Fora do normal", alarme: "Alarme", gemeo: "Gêmeo digital", sem_comunicacao: "Sem comunicação",
  outorga: "Outorga (CNARH)" };  // v1.2: US-3702
const GESTOR: Record<Gatilho["investigacao"], string> = { pendente: "na fila do gestor", em_andamento: "investigando…",
  concluida: "diagnóstico pronto", sem_orcamento: "só o aviso (limite de gasto ou gestor indisponível)", dispensada: "sem investigação" };
const quando = (iso: string | null) => (iso ? new Date(iso).toLocaleString("pt-BR", { dateStyle: "short", timeStyle: "short" }) : "—");
const VAZIO = { medida: "*", desvio_min: "2", janelas_seguidas: "3", ativo: true, motivo: "" };

export function Gestor() {
  const { empresa } = useCco();
  const [gatilhos, setGatilhos] = useState<Gatilho[] | null>(null);
  const [encerrados, setEncerrados] = useState(false);
  const [detalhe, setDetalhe] = useState<Detalhe | null>(null);
  const [limiares, setLimiares] = useState<Limiares | null>(null);
  const [ultima, setUltima] = useState<Execucao | null>(null);
  const [ind, setInd] = useState<Indicadores | null>(null);
  const [form, setForm] = useState(VAZIO);
  const [erro, setErro] = useState<string | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const carregar = useCallback(() => {
    if (!empresa) return;
    api<{ gatilhos: Gatilho[] }>(`/v1/rondas/gatilhos?empresa=${empresa}&abertos=${!encerrados}&limite=200`)
      .then((r) => { setGatilhos(r.gatilhos); setErro(null); })
      .catch((x) => setErro(x instanceof ErroApi ? x.message : "Falha de rede."));
    api<{ execucoes: Execucao[] }>(`/v1/rondas/execucoes?empresa=${empresa}&limite=1`).then((r) => setUltima(r.execucoes[0] ?? null)).catch(() => undefined);
    api<Limiares>(`/v1/rondas/limiares?empresa=${empresa}`).then(setLimiares).catch(() => undefined);
    api<Indicadores>(`/v1/rondas/indicadores?empresa=${empresa}&dias=30`).then(setInd).catch(() => undefined);
  }, [empresa, encerrados]);
  useEffect(() => { carregar(); const r = window.setInterval(carregar, 60000); return () => window.clearInterval(r); }, [carregar]);

  const abrir = (g: Gatilho) => {
    if (!empresa) return;
    api<Detalhe>(`/v1/rondas/gatilhos/${g.id}?empresa=${empresa}`).then(setDetalhe)
      .catch((x) => setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."));
  };
  const opinar = async (util: boolean) => {
    if (!empresa || !detalhe) return;
    try {
      await api(`/v1/rondas/gatilhos/${detalhe.id}/feedback`, { corpo: { empresa, util } });
      setDetalhe({ ...detalhe, meu_feedback: util });
      setAviso("Obrigado! Sua opinião ajuda a calibrar a ronda.");
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };
  const salvar = async (e: FormEvent) => {
    e.preventDefault();
    if (!empresa) return;
    try {
      await api("/v1/rondas/limiares", { metodo: "PUT", corpo: { empresa, medida: form.medida.trim() || "*", ativo: form.ativo,
        desvio_min: Number(form.desvio_min.replace(",", ".")), janelas_seguidas: Number(form.janelas_seguidas), motivo: form.motivo } });
      setAviso(`Limiar de ${form.medida === "*" ? "todas as medidas" : form.medida} salvo: vale na próxima ronda.`);
      setForm(VAZIO);
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };
  const remover = async (l: Limiar) => {
    if (!empresa) return;
    const motivo = form.motivo.trim().length >= 5 ? form.motivo : "volta ao padrão pela tela";
    try {
      await api(`/v1/rondas/limiares?empresa=${empresa}&medida=${encodeURIComponent(l.medida)}&motivo=${encodeURIComponent(motivo)}`, { metodo: "DELETE" });
      setAviso(`${l.medida === "*" ? "Padrão da operadora" : l.medida} voltou ao padrão do sistema.`);
      carregar();
    } catch (x) { setAviso(x instanceof ErroApi ? `Não foi possível: ${x.message}` : "Falha de rede."); }
  };

  const contagem = (p: Prioridade) => (gatilhos ?? []).filter((g) => g.prioridade === p && !g.encerrado_em).length;
  const ultimaInv = detalhe?.investigacoes.find((i) => i.situacao === "concluida") ?? detalhe?.investigacoes[0];

  return (
    <section className="seguranca gestor-ronda" aria-label="Ronda do gestor">
      <div className="nx-cartao">
        <h3>Ronda do gestor</h3>
        <p className="nx-suave">A cada 15 minutos a plataforma confere a rede sozinha: ponto fora do normal para o dia da semana e o horário,
          alarme crítico ou alto, achado do gêmeo digital e equipamento sem comunicação. O gestor investiga os mais importantes e avisa
          quem está de plantão. O diagnóstico é só sugestão: decisão e manobra continuam no CCO Clássico.</p>
        <p className="nx-suave" role="status">Última ronda: {ultima ? `${quando(ultima.janela)}${ultima.erro ? " (com erro)" : ` · ${ultima.abertos} em aberto`}` : "—"}</p>
        <div className="contadores-seguranca" role="group" aria-label="Abertos por prioridade">
          {([1, 2, 3, 4] as Prioridade[]).map((p) => (
            <div key={p} className={`nx-cartao contador-seg prioridade-${p}`}>
              <span className="nx-kpi">{gatilhos ? contagem(p) : "—"}</span><small>{FORMA[p]} {NOME_PRIORIDADE[p]}</small>
            </div>
          ))}
        </div>
        {ind && (
          <p className="nx-suave indicadores-gestor" role="status" aria-label="Indicadores do gestor">
            Últimos {ind.dias} dias: {ind.gatilhos} gatilho(s), {ind.encerrados} normalizado(s)
            {ind.mediana_min_ate_normalizar !== null ? ` em ${ind.mediana_min_ate_normalizar.toLocaleString("pt-BR")} min (mediana)` : ""} ·
            {" "}úteis: {ind.feedback.taxa_utilidade_pct !== null ? `${ind.feedback.taxa_utilidade_pct.toLocaleString("pt-BR")}%` : "sem opiniões"}
            {" "}· falsos alertas: {ind.falsos_alertas} · curtos (1 ronda): {ind.curtos}
            {ind.antecedencia.casos ? ` · adiantou-se ao alarme em ${ind.antecedencia.casos} caso(s), ${ind.antecedencia.mediana_min?.toLocaleString("pt-BR")} min antes` : ""}
            {" "}· {ind.investigados} investigado(s), US$ {ind.custo_investigacoes_usd.toFixed(2)}
          </p>
        )}
        <label className="nx-suave"><input type="checkbox" checked={encerrados} onChange={(e) => setEncerrados(e.target.checked)} /> mostrar também os encerrados</label>
        {erro && <p className="erro" role="alert">{erro}</p>}
        {aviso && <p className="nx-cartao aviso-admin" role="status">{aviso}</p>}
        {!gatilhos && !erro && <p className="nx-suave">carregando…</p>}
        {gatilhos && (
          <div className="tabela-rolagem">
            <table className="tabela-composicao tabela-seguranca">
              <thead><tr><th>Prioridade</th><th>O que a ronda viu</th><th>Setor</th><th>Desde</th><th>Rondas</th><th>Gestor</th><th></th></tr></thead>
              <tbody>
                {gatilhos.map((g) => (
                  <tr key={g.id}>
                    <td><span className={`nx-selo prioridade-${g.prioridade}`}>{FORMA[g.prioridade]} {NOME_PRIORIDADE[g.prioridade]}</span></td>
                    <td><b>{g.resumo}</b><br /><small className="nx-suave">{TIPO[g.tipo]}{g.encerrado_em ? ` · encerrado ${quando(g.encerrado_em)}` : ""}</small></td>
                    <td>{g.setor || "—"}</td>
                    <td className="nx-valor">{quando(g.aberto_em)}</td>
                    <td className="nx-valor">{g.deteccoes}</td>
                    <td><small>{GESTOR[g.investigacao]}</small></td>
                    <td><button type="button" className="nx-botao" aria-label={`Ver ${g.resumo}`} onClick={() => abrir(g)}>
                      {g.investigacao === "concluida" ? "Ver diagnóstico" : "Ver"}</button></td>
                  </tr>
                ))}
                {!gatilhos.length && <tr><td colSpan={7} className="nx-suave">nada fora do normal agora</td></tr>}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {detalhe && (
        <div className="nx-cartao" role="region" aria-label="Diagnóstico do gestor">
          <h3>{detalhe.resumo}</h3>
          <p className="nx-suave">{TIPO[detalhe.tipo]} · {NOME_PRIORIDADE[detalhe.prioridade]} · aberto {quando(detalhe.aberto_em)} · visto em {detalhe.deteccoes} ronda(s)
            {detalhe.encerrado_em ? ` · voltou ao normal em ${detalhe.duracao_min?.toLocaleString("pt-BR")} min` : " · ainda em aberto"}</p>
          {!ultimaInv && <p>{GESTOR[detalhe.investigacao]}.</p>}
          {ultimaInv && ultimaInv.situacao !== "concluida" && <p>{GESTOR[detalhe.investigacao]}{ultimaInv.erro ? ` — ${ultimaInv.erro}` : ""}.</p>}
          {ultimaInv && ultimaInv.situacao === "concluida" && (
            <>
              {ultimaInv.hipoteses.length > 0 && (<><h4>Hipóteses (da mais provável)</h4><ol>{ultimaInv.hipoteses.map((h) => <li key={h}>{h}</li>)}</ol></>)}
              <p className="nx-selo">Confiança: {ultimaInv.confianca ?? "baixa"}</p>
              <div className="resposta-gestor" style={{ whiteSpace: "pre-wrap" }}>{ultimaInv.texto}</div>
              {ultimaInv.limitacoes.length > 0 && (<><h4>Limitações</h4><ul>{ultimaInv.limitacoes.map((l) => <li key={l}>{l}</li>)}</ul></>)}
              {ultimaInv.fontes.length > 0 && <p className="nx-suave">Fontes: {ultimaInv.fontes.join(" · ")}</p>}
              <p className="nx-suave">Investigado {quando(ultimaInv.criado_em)} · US$ {ultimaInv.custo_usd.toFixed(4)} · só sugestão: decisão e manobra no CCO Clássico.</p>
            </>
          )}
          {detalhe.pode_opinar && (
            <div className="acoes-feedback" role="group" aria-label="Ajudou?">
              <span>Ajudou? </span>
              <button type="button" className="nx-botao" aria-pressed={detalhe.meu_feedback === true} onClick={() => void opinar(true)}>👍 Útil</button>
              <button type="button" className="nx-botao" aria-pressed={detalhe.meu_feedback === false} onClick={() => void opinar(false)}>👎 Não útil</button>
            </div>
          )}
          <button type="button" className="nx-botao" onClick={() => setDetalhe(null)}>Fechar</button>
        </div>
      )}

      {limiares && (
        <div className="nx-cartao" role="region" aria-label="Sensibilidade da ronda">
          <h3>Sensibilidade da ronda</h3>
          <p className="nx-suave">Padrão do sistema: desvio de {limiares.padrao.desvio_min.toLocaleString("pt-BR")} e {limiares.padrao.janelas_seguidas} janelas de 15 min
            seguidas ({limiares.padrao.janelas_seguidas * 15} min). Desvio maior ou mais janelas = menos avisos.</p>
          <table className="tabela-composicao">
            <thead><tr><th>Medida</th><th>Desvio</th><th>Janelas</th><th>Ativo</th><th>Motivo</th>{limiares.pode_editar && <th></th>}</tr></thead>
            <tbody>
              {limiares.limiares.map((l) => (
                <tr key={l.medida}><td>{l.medida === "*" ? "todas (padrão da operadora)" : l.medida}</td><td>{l.desvio_min.toLocaleString("pt-BR")}</td>
                  <td>{l.janelas_seguidas}</td><td>{l.ativo ? "sim" : "não"}</td><td><small>{l.motivo}</small></td>
                  {limiares.pode_editar && <td><button type="button" className="nx-botao" onClick={() => void remover(l)}>Voltar ao padrão</button></td>}</tr>
              ))}
              {!limiares.limiares.length && <tr><td colSpan={6} className="nx-suave">vale o padrão do sistema para todas as medidas</td></tr>}
            </tbody>
          </table>
          {limiares.pode_editar && (
            <form className="form-filial" aria-label="Limiar da ronda" onSubmit={(e) => void salvar(e)}>
              <div className="nx-campo"><label htmlFor="rl-medida">Medida (* = todas)</label>
                <input id="rl-medida" required pattern="\*|[a-z0-9_]{1,60}" value={form.medida} onChange={(e) => setForm({ ...form, medida: e.target.value })} /></div>
              <div className="nx-campo"><label htmlFor="rl-desvio">Desvio mínimo</label>
                <input id="rl-desvio" required inputMode="decimal" value={form.desvio_min} onChange={(e) => setForm({ ...form, desvio_min: e.target.value })} /></div>
              <div className="nx-campo"><label htmlFor="rl-janelas">Janelas seguidas (1–8)</label>
                <input id="rl-janelas" required type="number" min={1} max={8} value={form.janelas_seguidas} onChange={(e) => setForm({ ...form, janelas_seguidas: e.target.value })} /></div>
              <div className="nx-campo"><label htmlFor="rl-ativo">Ronda nesta medida</label>
                <select id="rl-ativo" value={form.ativo ? "sim" : "nao"} onChange={(e) => setForm({ ...form, ativo: e.target.value === "sim" })}>
                  <option value="sim">ligada</option><option value="nao">desligada</option></select></div>
              <div className="nx-campo"><label htmlFor="rl-motivo">Motivo</label>
                <input id="rl-motivo" required minLength={5} maxLength={300} value={form.motivo} placeholder="ex.: pressão oscila muito à noite"
                  onChange={(e) => setForm({ ...form, motivo: e.target.value })} /></div>
              <button type="submit" className="nx-botao">Salvar limiar</button>
            </form>
          )}
        </div>
      )}
    </section>
  );
}
